import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Coins, Activity, Globe2, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { PageHeader, Section } from "../components/AiHtml";
import { ReportButton } from "../components/ReportButton";
import { AuditComparison } from "../components/AuditComparison";
import { useBrands } from "../context/BrandContext";

const QUICK = [
  ["/social", "Social Media", "7-day posts with images"],
  ["/research", "Research & PPT", "Market research + 6-slide decks"],
  ["/auditor", "SEO AEO GEO", "Audit + schema JSON-LD"],
  ["/ads", "Ads Manager", "Google, Meta, LinkedIn, ChatGPT"],
  ["/content", "Content Studio", "Blogs, articles, social"],
  ["/email", "Email Studio", "Responsive HTML emails"],
  ["/website", "Website Builder", "Code + live preview"],
  ["/image", "Image Studio", "1K / 2K / 4K visuals"],
];

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="card-n p-5" data-testid={testId}>
    <div className="flex items-center justify-between"><p className="label-mono">{label}</p><Icon size={16} className="text-slate-500" /></div>
    <p className="text-3xl font-display font-bold mt-3">{value}</p>
  </div>
);

export default function Dashboard() {
  const { user, config } = useAuth();
  const { active: brand } = useBrands();
  const [usage, setUsage] = useState({ usage: [], ledger: [] });
  useEffect(() => {
    api.get("/me/usage").then((r) => setUsage(r.data));
  }, []);
  const a = brand?.auditReport;
  const spent = usage.usage.reduce((s, x) => s + x.cost, 0);

  return (
    <div className="space-y-8">
      <PageHeader label="Command center" title={`Hello, ${user.name.split(" ")[0]}`} desc={brand ? `${brand.businessName} · ${brand.city} · ${brand.website}` : "No brand yet"}
        right={<ReportButton params={brand ? { brandId: brand.brandId } : {}} />} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Coins} label="Credits" value={user.isAdmin ? "Unlimited" : user.credits.toLocaleString()} testId="dash-credits" />
        <Stat icon={Activity} label="Tasks run" value={usage.usage.length} testId="dash-tasks" />
        <Stat icon={Coins} label="Credits used" value={spent} testId="dash-spent" />
        <Stat icon={Globe2} label="SEO score" value={a?.seo_score ?? "—"} testId="dash-seo" />
      </div>
      {brand?.auditHistory?.length > 1 && <AuditComparison brand={brand} />}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Your AI team" right={<Link to="/employees" className="text-xs text-orange-400 flex items-center gap-1" data-testid="dash-view-team">View all <ArrowUpRight size={13} /></Link>}>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {(config?.employees || []).map((e) => (
                <Link key={e.id} to={`/employees/${e.id}`} data-testid={`dash-employee-${e.id}`} className="p-3 rounded-lg border border-slate-800 hover:border-orange-500/60 transition-colors">
                  <div className="relative w-9 h-9 rounded-full bg-orange-500/15 text-orange-400 grid place-items-center font-display font-bold">{e.name[0]}<span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0F172A]" /></div>
                  <p className="text-sm font-medium mt-2 flex items-center gap-1.5">{e.name}<span className="text-[10px] text-emerald-400">Active</span></p>
                  <p className="text-[11px] text-slate-500 leading-tight">{e.role}</p>
                </Link>
              ))}
            </div>
          </Section>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {QUICK.map(([to, t, d]) => (
              <Link key={to} to={to} data-testid={`dash-quick-${to.slice(1)}`} className="card-n p-4 group hover:border-orange-500/50 transition-colors">
                <p className="font-medium text-sm flex items-center justify-between">{t}<ArrowUpRight size={14} className="text-slate-600 group-hover:text-orange-400 transition-colors" /></p>
                <p className="text-xs text-slate-500 mt-1">{d}</p>
              </Link>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          {a && (
            <Section title="Latest audit">
              <p className="text-sm text-slate-400 line-clamp-4">{a.summary}</p>
              <div className="grid grid-cols-2 gap-2 mt-4 text-xs font-mono">
                {[["SEO", a.seo_score], ["AEO", a.aeo_score], ["GEO", a.geo_score], ["Social", a.social_score]].map(([l, v]) => (
                  <div key={l} className="rounded bg-slate-900 p-2 flex justify-between"><span className="text-slate-500">{l}</span><span className="text-orange-400">{v}</span></div>
                ))}
              </div>
              <Link to="/auditor" className="text-xs text-orange-400 mt-4 inline-block" data-testid="dash-open-audit">Open full report →</Link>
            </Section>
          )}
          <Section title="Recent activity" testId="dash-activity">
            <ul className="space-y-2 text-sm max-h-72 overflow-auto scroll-thin">
              {usage.usage.length === 0 && <li className="text-slate-500">No tasks yet.</li>}
              {usage.usage.slice(0, 12).map((x) => (
                <li key={x.logId} className="flex justify-between"><span className="text-slate-300 capitalize">{x.task.replace("_", " ")}</span><span className="font-mono text-xs text-slate-500">{x.demo ? "demo" : `-${x.cost}`} · {new Date(x.date).toLocaleDateString()}</span></li>
              ))}
            </ul>
          </Section>
          <Section title="Credit ledger">
            <ul className="space-y-2 text-sm max-h-48 overflow-auto scroll-thin">
              {usage.ledger.map((x) => (
                <li key={x.entryId} className="flex justify-between gap-2"><span className="text-slate-400 truncate">{x.reason}</span><span className={`font-mono text-xs ${x.creditsAdded >= 0 ? "text-emerald-400" : "text-red-400"}`}>{x.creditsAdded > 0 ? "+" : ""}{x.creditsAdded}</span></li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
}
